import * as React from 'react';
import {Query} from 'react-apollo';
import {Helmet} from 'react-helmet';

import {formEncodedApi} from 'nutshell-core/api';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {headTitle} from 'shells/document/head-title';

import type {
    SalesAutomationStageset as SalesAutomationStagesetFragment,
    StagesetAndStages as StagesetAndStagesQuery,
    StagesetAndStagesVariables as StagesetAndStagesQueryVariables,
} from 'nutshell-graphql-types';

import GET_STAGESET_AND_STAGES from '../graphql/queries/get-stageset-and-stages.graphql';
import {OnboardingModalCompanyTypes} from '../onboarding/new-features/onboarding-modal-company-types';
import type {AvailableIntegrations} from '../types';

import {ClosedLeadOutcomeHeaders} from './header/closed-lead-outcome-headers';
import {SalesAutomationClosedLeadsBoard} from './sales-automation-closed-leads-board';
import {SalesAutomationAppShell} from './shell/sales-automation-app-shell';

type OwnProps = {
    stagesetId: string;
};

type GraphQLProps = {
    isLoading: boolean;
    availableIntegrations: AvailableIntegrations;
    errorMessage: string | null | undefined;
    stageset: SalesAutomationStagesetFragment | null | undefined;
    onRefetchStageset: () => any;
};

type Props = OwnProps & GraphQLProps;

type State = {
    isOnboardingCompanyTypes: boolean;
};

/*
 * Renders the closed lead board within the SalesAutomationAppShell, which
 * provides the navigation and editing sidebars.
 */
export class SalesAutomationClosedLeadsComponent extends React.PureComponent<Props, State> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isOnboardingCompanyTypes: NutClientConfig.shouldShowCompanyTypeAutomationOnboarding,
        };
    }

    render() {
        const {stageset} = this.props;
        if (!stageset) return null;

        return (
            <React.Fragment>
                <Helmet>
                    <title>{headTitle(stageset.name)}</title>
                </Helmet>
                <SalesAutomationAppShell
                    stageset={stageset}
                    headers={<ClosedLeadOutcomeHeaders />}
                    board={
                        <SalesAutomationClosedLeadsBoard
                            stagesetId={this.props.stagesetId}
                            closedLeadAutomations={
                                stageset.editableProcessTemplate.closedLeadAutomations
                            }
                            errorMessage={this.props.errorMessage}
                            availableIntegrations={this.props.availableIntegrations}
                            isLoading={this.props.isLoading}
                            onRefetchStageset={this.props.onRefetchStageset}
                        />
                    }
                />
                <OnboardingModalCompanyTypes
                    isOpen={this.state.isOnboardingCompanyTypes}
                    onClose={this.handleClose}
                />
            </React.Fragment>
        );
    }

    handleClose = () => {
        // Save our "has viewed" setting to the server
        formEncodedApi.post('/my-account/save-user-setting.json', {
            setting: 'has_viewed_onboarding_sales_automation_company_types_NUT-10534',
            value: 1,
        });

        // Flip local state so we hide our modal
        this.setState({
            isOnboardingCompanyTypes: false,
        });

        // Manually update local state in case this component re-mounts
        NutClientConfig.shouldShowCompanyTypeAutomationOnboarding = false;
    };
}

class StagesQuery extends Query<StagesetAndStagesQuery, StagesetAndStagesQueryVariables> {}

export const SalesAutomationClosedLeads = (props: OwnProps) => (
    <StagesQuery query={GET_STAGESET_AND_STAGES} variables={{stagesetId: props.stagesetId}}>
        {({data, loading, error, refetch}) => {
            const {
                stageset,
                hasMailChimpIntegration,
                hasSlackIntegration,
                // Needed for flow
                hasConstantContactIntegration,
            } = data || {};

            return (
                <SalesAutomationClosedLeadsComponent
                    {...props}
                    isLoading={loading}
                    stageset={stageset}
                    availableIntegrations={{
                        hasMailChimpIntegration,
                        hasSlackIntegration,
                        // Needed for flow
                        hasConstantContactIntegration,
                    }}
                    // @ts-expect-error
                    errorMessage={safelyGetGraphQLErrorMessage(error)}
                    onRefetchStageset={refetch}
                />
            );
        }}
    </StagesQuery>
);
